import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { formatPrice } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotalInr } = useCart();
  const { currency } = useCurrency();
  const navigate = useNavigate();

  return (
    <>
      {open && <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)} data-testid="cart-overlay" />}
      <aside
        className={`fixed z-50 top-0 right-0 h-full w-full sm:w-[460px] bg-[#FDFBF7] transform transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#BCA37F]/25">
          <p className="font-serif text-xl">Your Bag</p>
          <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart">
            <X strokeWidth={1.3} />
          </button>
        </div>
        <div className="overflow-y-auto h-[calc(100%-8rem)]">
          {items.length === 0 ? (
            <div className="p-8 text-center text-[#7A7A7A]">
              <p className="font-serif text-2xl text-[#1A1A1A] mb-2">Your bag is empty.</p>
              <p className="text-sm">Discover new arrivals in the atelier.</p>
              <button
                onClick={() => { setOpen(false); navigate("/collections/festive"); }}
                className="mt-6 btn-editorial-ghost"
                data-testid="cart-empty-shop"
              >
                Browse Collection
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-[#BCA37F]/20">
              {items.map((i) => (
                <li key={`${i.product_id}__${i.size}`} className="p-5 flex gap-4" data-testid={`cart-line-${i.product_id}-${i.size}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-28 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight">{i.name}</p>
                    <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">Size: {i.size}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="p-1 text-[#5C1A1B]" data-testid={`cart-dec-${i.product_id}-${i.size}`}><Minus size={14} /></button>
                      <span className="text-sm min-w-[1.5rem] text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="p-1 text-[#5C1A1B]" data-testid={`cart-inc-${i.product_id}-${i.size}`}><Plus size={14} /></button>
                      <button onClick={() => removeItem(i.product_id, i.size)} className="ml-auto text-[11px] uppercase tracking-nav text-[#7A7A7A] hover:text-[#5C1A1B]" data-testid={`cart-remove-${i.product_id}-${i.size}`}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-[#1A1A1A] whitespace-nowrap">{formatPrice(i.price_inr * i.quantity, currency)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="absolute bottom-0 inset-x-0 border-t border-[#BCA37F]/25 bg-[#FDFBF7] p-5">
            <div className="flex justify-between text-sm mb-3">
              <span className="uppercase tracking-nav text-[#7A7A7A]">Subtotal</span>
              <span className="font-mono-alt text-[#1A1A1A]" data-testid="cart-subtotal">{formatPrice(subtotalInr, currency)}</span>
            </div>
            <button
              onClick={() => { setOpen(false); navigate("/checkout"); }}
              className="btn-editorial w-full"
              data-testid="cart-checkout"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
